import { SupportedLocale } from '../constants'
import { NumericDBType } from '../definitions/field-definitions/numeric-db-type'

type SeparatorType = 'decimal' | 'group'
const defaultSeparator: Record<SeparatorType, string> = {
  decimal: ',',
  group: '',
}

const MIN_NUMBER = 1000.1

export function getSeparator(lang: SupportedLocale, separatorType: SeparatorType, n?: number): string {
  let numberWithGroupAndDecimalSeparator = n ?? MIN_NUMBER
  if (numberWithGroupAndDecimalSeparator < MIN_NUMBER) {
    numberWithGroupAndDecimalSeparator = MIN_NUMBER
  }
  if (Math.round(numberWithGroupAndDecimalSeparator) === numberWithGroupAndDecimalSeparator) {
    numberWithGroupAndDecimalSeparator += 0.1
  }
  const locale = Intl.NumberFormat(lang)
  const formatted = locale.formatToParts(numberWithGroupAndDecimalSeparator)
  const separator =
    (formatted.find((part) => part.type === separatorType) as Intl.NumberFormatPart)?.value ??
    defaultSeparator[separatorType]
  return separator
}

export type DecimalInfo = {
  decimalLimit: number
  requiredDecimal: number
  optionalDecimal: number
}

export type NumericSafeValueLimits = {
  min: number
  max: number
}

export function getDecimalInfo({
  format,
  dbType,
  scale,
}: {
  format: string
  dbType: NumericDBType
  scale: number
}): DecimalInfo | undefined {
  if (dbType === NumericDBType.Decimal) {
    return {
      decimalLimit: scale,
      requiredDecimal: 0,
      optionalDecimal: scale,
    }
  }

  if (!format) return

  let [, fraction] = format.split('.')
  if (typeof fraction != 'string') fraction = ''
  const requiredDecimal = (fraction.match(/0/g) || []).length
  const optionalDecimal = (fraction.match(/#/g) || []).length
  const decimalLimit = requiredDecimal + optionalDecimal

  return {
    decimalLimit,
    requiredDecimal,
    optionalDecimal,
  }
}

export function getSafeValueLimits(
  dbType: NumericDBType = NumericDBType.Float,
  precision = 18,
  scale = 5,
): NumericSafeValueLimits {
  switch (dbType) {
    case NumericDBType.Float:
      return {
        min: -Number.MAX_VALUE,
        max: Number.MAX_VALUE,
      }
    case NumericDBType.Decimal:
      return calculateDecimalRange(precision, scale)
    case NumericDBType.Int:
      return {
        min: -2147483648,
        max: 2147483647,
      }
    default:
      return {
        min: -Number.MAX_VALUE,
        max: Number.MAX_VALUE,
      }
  }
}

function calculateDecimalRange(precision: number, scale: number): NumericSafeValueLimits {
  const integerDigits = precision - scale

  const intPart = integerDigits > 0 ? '9'.repeat(integerDigits) : '0'
  const fracPart = scale > 0 ? '9'.repeat(scale) : ''
  const maxStr = scale > 0 ? `${intPart}.${fracPart}` : intPart
  const max = Number(maxStr)

  return {
    min: -max,
    max,
  }
}
